import React, { useCallback, useState, memo } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal } from 'data/reducers/modalReducer';
import StandardModal from '../StandardModal';

/**
 * AcceptInvitationModal
 *
 * Shown when accepting an organization invitation comes back with the
 * REQUIRES_CONFIRMATION outcome -- the invitee already has their own
 * (free, provisional) organization. Copy is verbatim from the product
 * spec. `propertyCount === 0` drops the first paragraph and keeps only
 * the closing line, since there is nothing to warn about losing.
 *
 * Confirming re-calls `acceptInvitation` with `confirmed: true` via the
 * `onConfirm` callback the caller (AcceptInvitation.js) supplies.
 */

const SINGULAR_PROPERTY_COUNT = 1;

/**
 * Builds the count-plus-noun phrase for the property warning, singular at
 * exactly one property and plural otherwise. The repo carries no
 * pluralization or i18n helper, so this stays local to the copy it serves.
 */
const propertyCountPhrase = (count) =>
	`${count} ${count === SINGULAR_PROPERTY_COUNT ? 'property' : 'properties'}`;

const AcceptInvitationModal = memo(({ options }) => {
	const dispatch = useDispatch();
	const organizationName = options?.organizationName;
	const propertyCount = options?.propertyCount || 0;
	const onConfirm = options?.onConfirm;

	const [isConfirming, setIsConfirming] = useState(false);

	const handleCancel = useCallback(() => {
		dispatch(closeModal());
	}, [dispatch]);

	const handleConfirm = useCallback(() => {
		setIsConfirming(true);
		dispatch(closeModal());
		if (onConfirm) {
			onConfirm();
		}
	}, [dispatch, onConfirm]);

	return (
		<StandardModal
			title={`Join ${organizationName}?`}
			size="md"
			onClose={handleCancel}
			noFooter
		>
			<div className="modal-body">
				{propertyCount > 0 && (
					<p>
						You currently have {propertyCountPhrase(propertyCount)}. These
						won't move over &mdash; {organizationName}'s admin will need to
						add them to the organization.
					</p>
				)}
				<p>Your free account will close when you join.</p>
			</div>

			<div className="modal-footer">
				<button
					type="button"
					className="btn btn-secondary"
					onClick={handleCancel}
					disabled={isConfirming}
				>
					Cancel
				</button>
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleConfirm}
					disabled={isConfirming}
				>
					Join organization
				</button>
			</div>
		</StandardModal>
	);
});

AcceptInvitationModal.displayName = 'AcceptInvitationModal';

export default AcceptInvitationModal;
